import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MngMessageService } from '@mng/mng-message-handler';
import { firstValueFrom, Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { getFilenameFromHeader } from '@mng/mng-ts-helpers';

export interface BlobWithFilename {
  blob: Blob;
  filename: string;
}

@Service()
export class MngHttpService {
  private readonly http = inject(HttpClient);
  private readonly messages = inject(MngMessageService);

  baseUrl = this.examineBaseUrl();

  preventErrorHandling = false;
  preventAdaptUrl = false;

  setPreventErrorHandling(preventErrorHandling: boolean) {
    this.preventErrorHandling = preventErrorHandling;
  }

  setPreventAdaptUrl(preventAdaptUrl: boolean) {
    this.preventAdaptUrl = preventAdaptUrl;
  }

  custom<T = any>(
    method: string,
    url: string,
    body: any,
    reportProgress = false,
    preventErrorHandling = false,
    preventLoadingIndicator = false
  ) {
    const headers = createDefaultRequestHeaders();
    const req = new HttpRequest(method, this.adaptUrl(url), body, {
      reportProgress: reportProgress,
      headers: headers
    });

    return this.executeRequest<T>(this.http.request<T>(req), preventErrorHandling, preventLoadingIndicator);
  }

  delete<T>(url: string, body?: any, preventErrorHandling = false, preventLoadingIndicator = false): Observable<T> {
    const headers = createDefaultRequestHeaders();
    return this.executeRequest(
      this.http.delete(this.adaptUrl(url), { headers: headers, body: body }),
      preventErrorHandling,
      preventLoadingIndicator
    );
  }

  get<T>(
    url: string,
    preventErrorHandling = false,
    preventLoadingIndicator = false,
    headers: HttpHeaders = createDefaultRequestHeaders()
  ): Observable<T> {
    return this.executeRequest(
      this.http.get(this.adaptUrl(url), { headers: headers }),
      preventErrorHandling,
      preventLoadingIndicator
    );
  }

  getWithParams<T>(
    url: string,
    params: HttpParams,
    preventErrorHandling = false,
    preventLoadingIndicator = false,
    headers: HttpHeaders = createDefaultRequestHeaders()
  ): Observable<T> {
    return this.executeRequest(
      this.http.get(this.adaptUrl(url), { params: params, headers: headers }),
      preventErrorHandling,
      preventLoadingIndicator
    );
  }

  async getBlob(
    url: string,
    defaultFileName = 'file',
    preventErrorHandling = true,
    preventLoadingIndicator = true
  ): Promise<BlobWithFilename> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Accept', '*/*');
    const response = await firstValueFrom(
      this.executeRequest<HttpResponse<Blob>>(
        this.http.get(this.adaptUrl(url), { headers, observe: 'response', responseType: 'blob' }),
        preventErrorHandling,
        preventLoadingIndicator
      )
    );
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition ? getFilenameFromHeader(contentDisposition, defaultFileName) : defaultFileName;
    return { blob: response.body, filename };
  }

  async getBlobWithPost(
    url: string,
    data: unknown,
    defaultFileName = 'file',
    preventErrorHandling = true,
    preventLoadingIndicator = true
  ): Promise<BlobWithFilename> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Accept', '*/*');
    const response = await firstValueFrom(
      this.executeRequest<HttpResponse<Blob>>(
        this.http.post(this.adaptUrl(url), data, { headers, observe: 'response', responseType: 'blob' }),
        preventErrorHandling,
        preventLoadingIndicator
      )
    );
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition ? getFilenameFromHeader(contentDisposition, defaultFileName) : defaultFileName;
    return { blob: response.body, filename };
  }

  async getTextAsPromise(url: string) {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Accept', '*/*');
    const response = await firstValueFrom(
      this.http.get(this.adaptUrl(url), { headers, observe: 'response', responseType: 'text' })
    );
    return response.body;
  }

  post<T>(
    url: string,
    body: any = {},
    preventErrorHandling = false,
    preventLoadingIndicator = false,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe?: any;
      params?:
        | HttpParams
        | {
            [param: string]: string | string[];
          };
      reportProgress?: boolean;
      responseType?: any;
      withCredentials?: boolean;
    }
  ): Observable<T> {
    // const jsonString = JSON.stringify(params);

    // Attention -> subscribe() on the Observable executes the thing
    // because of Observables are lazy ....
    // to intercept the request for result handling "do(...)" is used
    return this.executeRequest(
      this.http.post(this.adaptUrl(url), body, { headers: createDefaultRequestHeaders(), ...options }),
      preventErrorHandling,
      preventLoadingIndicator
    );
  }

  put<T>(url: string, body: any, preventErrorHandling = false, preventLoadingIndicator = false): Observable<T> {
    const headers = createDefaultRequestHeaders();

    // Attention -> subscribe() on the Observable executes the thing
    // because of Observables are lazy ....
    // to intercept the request for result handling "do(...)" is used
    return this.executeRequest(
      this.http.put(this.adaptUrl(url), body, { headers: headers }),
      preventErrorHandling,
      preventLoadingIndicator
    );
  }

  examineBaseUrl(): string {
    let baseUrl: string;
    try {
      const htmlElement: HTMLElement = document.getElementsByTagName('base')[0];
      baseUrl = htmlElement && htmlElement.attributes ? htmlElement.attributes.getNamedItem('href').nodeValue : '';
    } catch (e) {
      // console.error(e);
    }
    // console.log('baseUrl', baseUrl);
    return baseUrl;
  }

  adaptUrl(url: string): string {
    if (this.baseUrl && !this.preventAdaptUrl && this.baseUrl !== '/') {
      return `../${url}`;
    } else {
      return url;
    }
  }

  /**
   * Wraps an HTTP observable with centralized message/error handling and loading-indicator lifecycle.
   *
   * Behavior:
   * - Sends `{ inProgress: true }` immediately when this method is called, unless
   *   `preventLoadingIndicator` is `true`.
   * - Forwards `next` notifications to `createResultHandler()`.
   * - Forwards `error` notifications to `createErrorHandler(preventErrorHandling)`.
   * - Uses `finalize(...)` to always send `{ inProgress: false }` when the stream terminates
   *   (complete, error, or unsubscribe), unless `preventLoadingIndicator` is `true`.
   *
   * @template T Expected response payload type.
   * @param request Source HTTP observable.
   * @param preventErrorHandling If `true`, suppresses default error message handling.
   * @param preventLoadingIndicator If `true`, skips in-progress start/stop messages.
   * @returns Observable that mirrors `request` with side effects for messaging/loading state.
   */
  executeRequest<T>(
    request: Observable<any>,
    preventErrorHandling: boolean,
    preventLoadingIndicator: boolean
  ): Observable<T> {
    if (!preventLoadingIndicator) {
      this.sendInProgressMessage(true);
    }

    return request.pipe(
      tap({
        next: this.createResultHandler().bind(this),
        error: this.createErrorHandler(preventErrorHandling).bind(this)
      }),
      finalize(() => {
        if (!preventLoadingIndicator) {
          this.sendInProgressMessage(false);
        }
      })
    );
  }

  sendInProgressMessage(inProgress: boolean): void {
    this.messages.pushValue({ inProgress: inProgress });
  }

  createResultHandler(error = false, preventErrorHandling = false) {
    return (response) => {
      // Loading indicator is finalized in executeRequest.finalize(), independent of payload shape.
      try {
        if (response instanceof HttpResponse || response.result) {
          if (response && response.message && response.message.message) {
            this.messages.pushValue(response.message);
          } else if (error && !preventErrorHandling) {
            this.messages.pushValue({ message: 'Ein Serverfehler ist aufgetreten!', type: 'error' });
          }
        } else if (response instanceof HttpErrorResponse && !preventErrorHandling && !this.preventErrorHandling) {
          this.messages.pushValue(response);
        }
      } catch (e) {
        this.messages.pushValue(response);
      }
    };
  }

  createErrorHandler(preventErrorHandling: boolean) {
    return this.createResultHandler(true, preventErrorHandling);
  }

  // TODO: refactor?
  postWithInProgressMessage(observable: Observable<any>): Observable<any> {
    this.sendInProgressMessage(true);
    return observable.pipe(
      tap(
        () => {
          this.sendInProgressMessage(false);
        },
        () => {
          this.sendInProgressMessage(false);
        }
      )
    );
  }
}

export function createRequestHeaders(accept: string): HttpHeaders {
  let headers: HttpHeaders = new HttpHeaders();
  headers = headers.append('Accept', accept);
  return headers;
}

export function createDefaultRequestHeaders(): HttpHeaders {
  return createRequestHeaders('application/json');
}

export function createPdfRequestHeaders(): HttpHeaders {
  return createRequestHeaders('application/pdf');
}
